import React from 'react';
import ReactDOM from 'react-dom';

import * as analytics from 'analytics/Analytics';
import BufferTabView from 'buffers/BufferTabView';
import {Sequence} from 'components/Sequence';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import Protein from 'molbio/proteins/Protein';
import ProteinActions from 'molbio/proteins/ProteinActions';
import VisualHistoryTabContents from 'molbio/visual-history/VisualHistoryTabContents';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';

import VisualHistoryTabState from './VisualHistoryTabState';

export default class VisualHistoryTabView<
  Polymer extends Sequence | Protein,
  M extends VisualHistoryTabState<Polymer>,
> extends BufferTabView<M> {
  viewedEvent = MOLBIO_ANALYTICS_ACTIONS.VIEWED_VISUAL_HISTORY_TAB;

  override initialize(): void {
    super.initialize();
    this.registerListeners();
  }

  override afterAttach(): void {
    super.afterAttach();
    analytics.trackEvent(this.viewedEvent, {
      polymerId: this.model.polymer().id,
    });
  }

  override render(): this {
    let entityType;
    if (this.getPolymer() instanceof Sequence) {
      entityType = 'sequence';
    } else {
      entityType = 'protein';
    }
    renderToDOM(
      <VisualHistoryTabContents
        entityId={this.getPolymer().id}
        entityType={entityType}
        entityVersionId={this.getPolymer().getActiveVersionId()}
        onClickVersion={this.onClickVersion.bind(this)}
      />,
      this.el
    );
    return this;
  }

  registerListeners(): void {
    // Listen for change events on both protein and sequence models which store their open version ID
    // and "staleness" indicator under different attribute names.
    this.listenTo(
      this.getPolymer(),
      'change:activeVersionId change:currentVersion change:protein',
      this.render.bind(this)
    );
  }

  /**
   * Callback to open a specific version of the polymer.
   * @param polymerVersionId Id of the version of the polymer.
   */
  onClickVersion(polymerVersionId: number): void {
    const polymer = this.getPolymer();
    if (polymer instanceof Sequence) {
      const version = polymer.get('versions').findWhere({id: polymerVersionId});
      polymer.viewVersion(version);
    } else {
      ProteinActions.open(polymer.id, {versionId: polymerVersionId});
    }
  }

  getPolymer(): Polymer {
    return this.model.get('polymer');
  }

  override remove(): this {
    ReactDOM.unmountComponentAtNode(this.el);
    super.remove();
    return this;
  }
}
